// @ts-nocheck
namespace $ {
	
	type DuckDB = typeof import('./.npm/.app')
	type AsyncDuckDB = InstanceType< DuckDB['AsyncDuckDB'] >
	type AsyncDuckDBConnection = Awaited< ReturnType< AsyncDuckDB['connect'] > >
	type DuckDBBundle = import('./.npm/.app').DuckDBBundle

		export class $shm_duckdb extends $mol_object2 {

		static _db: AsyncDuckDB
		static _db_promise: Promise< AsyncDuckDB > | null = null
		static _file_urls = new Map< string, string >
		static _bundle_urls = new Map< string, string >
		static _table_writes = new Map< string, Promise< unknown > >
		static _file_tasks = new Map< string, Promise<void> >
		static _persistent_files = new Set<string>
		static _generation = 0
		static _startup_cancel: ( ()=> void ) | null = null

		@ $mol_mem
		static lib() {
			$mol_wire_solid()
			return $mol_import.script( 'shm/duckdb/bundle/app.js' ).$shm_duckdb_npm as DuckDB
		}

		static lib_async() {
			return $mol_wire_async( this ).lib()
		}

		@ $mol_mem
		static db() {
			this.db_generation()
			return $mol_wire_sync( this ).db_async()
		}

		@ $mol_mem
		static db_generation( next?: number ) {
			return next ?? 0
		}

		static async db_async() {
			if( this._db ) return this._db
			if( this._db_promise ) return this._db_promise

			const generation = this._generation
			const pending = ( async ()=> {
				const lib = await this.lib_async()
				const bundle = await lib.selectBundle( this.local_bundles() )
				if( generation !== this._generation ) throw new Error( 'DuckDB initialization was reset' )
				const db = await this.db_make( bundle )
				if( generation !== this._generation ) {
					await db.terminate()
					throw new Error( 'DuckDB initialization was reset' )
				}
				return this._db = db
			} )()
			this._db_promise = pending

			try {
				return await pending
			} finally {
				if( this._db_promise === pending ) this._db_promise = null
			}
		}

		static local_bundles() {
			const uri = ( path: string )=> new URL( path, $mol_dom_context.location.origin ).toString()
			return {
				mvp: {
					mainModule: uri( '/shm/duckdb/bundle/duckdb-mvp.wasm' ),
					mainWorker: uri( '/shm/duckdb/bundle/duckdb-browser-mvp.worker.js' ),
				},
				eh: {
					mainModule: uri( '/shm/duckdb/bundle/duckdb-eh.wasm' ),
					mainWorker: uri( '/shm/duckdb/bundle/duckdb-browser-eh.worker.js' ),
				},
			}
		}
		
		static async db_make( bundle: DuckDBBundle ) {
			if( !bundle.mainWorker ) throw new Error( 'DuckDB bundle has no mainWorker' )
			const lib = await this.lib_async()
			const worker_url = await this.bundle_url( bundle.mainWorker )
			const module_url = await this.bundle_url( bundle.mainModule )
			const worker = await lib.createWorker( worker_url )
			const logger = new lib.ConsoleLogger()
			const db = $shm_duckdb_worker.spawn( lib.AsyncDuckDB, logger, worker, error=> {
				reject_start?.( error )
				if( this._db === db ) void this.reset_async().catch( error=> console.error( 'DuckDB reset failed', error ) )
			} )
			let reject_start: ( error: Error )=> void
			const failed = new Promise< never >( ( resolve, reject )=> { reject_start = reject } )
			const cancel = ()=> reject_start( new Error( 'DuckDB initialization was reset' ) )
			this._startup_cancel = cancel
			const timer = setTimeout( ()=> reject_start( new Error( 'DuckDB initialization exceeded 60000ms. Retry the operation.' ) ), 60_000 )
			const startup_error = ( event: any )=> reject_start( new Error( event.message || 'DuckDB worker failed during initialization' ) )
			worker.addEventListener( 'error', startup_error )
			worker.addEventListener( 'messageerror', startup_error )

			try {
				await Promise.race([ db.instantiate( module_url, bundle.pthreadWorker ), failed ])
			} catch( error ) {
				db.abort( error as Error )
				await db.terminate()
				// A fetched script/module may be transiently broken. Retry must refetch it,
				// without evicting assets owned by a newer generation after reset.
				for( const [source,url] of [[bundle.mainWorker,worker_url],[bundle.mainModule,module_url]] ) {
					if( this._bundle_urls.get(source) !== url ) continue
					this._bundle_urls.delete(source)
					URL.revokeObjectURL(url)
				}
				throw error
			} finally {
				clearTimeout( timer )
				worker.removeEventListener( 'error', startup_error )
				worker.removeEventListener( 'messageerror', startup_error )
				if( this._startup_cancel === cancel ) this._startup_cancel = null
			}
			
			return db
		}

		static async bundle_url( source: string ) {
			const cached = this._bundle_urls.get( source )
			if( cached ) return cached

			const generation = this._generation
			const response = await $mol_dom_context.fetch( source, { signal: AbortSignal.timeout( 30_000 ) } )
			if( !response.ok ) throw new Error( `DuckDB asset ${ response.status }: ${ source }` )
			const original = await response.blob()
			if( generation !== this._generation ) throw new Error( 'DuckDB asset loading was reset' )
			// instantiateStreaming requires this MIME, including for blob: URLs.
			const blob = /\.wasm(?:[?#]|$)/i.test( source )
				? new Blob([ original ], { type: 'application/wasm' })
				: original
			const url = URL.createObjectURL( blob )
			this._bundle_urls.set( source, url )
			return url
		}

		static async reset_async() {
			++this._generation
			this.db_generation( this.db_generation() + 1 )
			this._startup_cancel?.()
			const db = this._db
			this._db = null as any
			this._db_promise = null
			this._file_tasks.clear()
			this._persistent_files.clear()
			this._table_writes.clear()

			for( const url of this._file_urls.values() ) URL.revokeObjectURL( url )
			this._file_urls.clear()
			for( const url of this._bundle_urls.values() ) URL.revokeObjectURL( url )
			this._bundle_urls.clear()

			if( db ) {
				;(db as any).abort( new Error( 'DuckDB operation was reset' ) )
				await db.terminate()
			}
		}

		
		static async file_query( file_name: string, buffer: Uint8Array, query: string ) {
			if(typeof file_name !== 'string' || !file_name.trim()) throw new Error('File title must be a non-empty string')
			return this.files_task_async([file_name],async()=>{
				this.files_temporary_check([file_name])
				const db = await this.db_async()
				await db.dropFile(file_name)
				await db.registerFileBuffer(file_name,buffer)
				let conn: AsyncDuckDBConnection | undefined
				try {
					conn = await db.connect()
					const table = await conn.query(query)
					return table.slice(0,100).toArray().map((row:any)=>row.toJSON())
				} finally {
					try {await conn?.close()}
					finally {await db.dropFile(file_name)}
				}
			})
		}

		static async connection() {
			const db = await this.db_async()
			return await db.connect()
		}

		static async connect_files_async( sources: any ) {
			const generation = this._generation
			await this.register_files_async(sources)
			if(generation !== this._generation) throw new Error('DuckDB operation was reset')
			return this.connection()
		}

		static async register_files_async( sources: any ) {
			const generation = this._generation
			const entries = await this.file_entries_async(sources)
			if(generation !== this._generation) throw new Error('DuckDB operation was reset')
			return this.files_task_async(entries.map(entry=>entry.name),async()=>{
				const db = await this.db_async()
				this.generation_check(generation)
				const registered: string[] = []
				try {
					await this.register_file_entries_async(db,entries,registered,generation)
					this.generation_check(generation)
					for(const name of registered) this._persistent_files.add(name)
				} catch(error) {
					const temporary = registered.filter(name=>!this._persistent_files.has(name))
					if(temporary.length && generation === this._generation) await db.dropFiles(temporary)
					throw error
				}
			})
		}

		static async file_entries_async( sources: any ) {
			if(!Array.isArray(sources)) throw new Error('File sources must be an array')
			const entries = new Map<string,{file:any,name:string,id?:string}>()
			for(const file of sources){
				if(!file || typeof file.title !== 'function' || typeof file.blob !== 'function') throw new Error('File source must expose title() and blob()')
				const name = await $mol_wire_async(file).title()
				if(typeof name !== 'string' || !name.trim()) throw new Error('File title must be a non-empty string')
				const id = typeof file.link === 'function' ? (await $mol_wire_async(file).link())?.str : undefined
				const previous = entries.get(name)
				if(previous && previous.file !== file && !(id && previous.id === id)) throw new Error(`Duplicate file title "${name}": use distinct titles for different sources`)
				if(!previous) entries.set(name,{file,name,id})
			}
			return [...entries.values()]
		}

		/** Reserve all overlapping paths at once; wait before registering or deleting them. */
		static async files_task_async<Result>( names: string[], task: ()=>Promise<Result> ): Promise<Result> {
			const generation = this._generation
			const keys = [...new Set(names)]
			const previous = keys.flatMap(name=>this._file_tasks.get(name)??[])
			let release!: ()=>void
			const held = new Promise<void>(resolve=>{release=resolve})
			for(const name of keys) this._file_tasks.set(name,held)
			try {
				await Promise.all(previous)
				if(generation !== this._generation) throw new Error('DuckDB operation was reset')
				return await task()
			} finally {
				release()
				for(const name of keys) if(this._file_tasks.get(name)===held) this._file_tasks.delete(name)
			}
		}

		static generation_check( generation: number ) {
			if(generation !== this._generation) throw new Error('DuckDB operation was reset')
		}

		static async register_file_entries_async( db: AsyncDuckDB, entries: {file:any,name:string}[], registered: string[] = [], generation = this._generation ) {
			for (const {file,name} of entries) {
				const blob = await $mol_wire_async(file).blob()
				this.generation_check(generation)
				if(!blob) throw new Error(`File "${name}" has no content`)
				const buf = await blob.arrayBuffer()
				this.generation_check(generation)
				await db.dropFile(name)
				this.generation_check(generation)
				await db.registerFileBuffer( name, new Uint8Array( buf ) )
				this.generation_check(generation)
				const old_url = this._file_urls.get(name)
				if(old_url) {URL.revokeObjectURL(old_url);this._file_urls.delete(name)}
				registered.push(name)
			}
		}

		static async register_files_url_async( sources: any ) {
			const generation = this._generation
			const entries = await this.file_entries_async(sources)
			if(generation !== this._generation) throw new Error('DuckDB operation was reset')
			return this.files_task_async(entries.map(entry=>entry.name),async()=>{
				const db = await this.db_async()
				return this.register_file_url_entries_async(db,entries,generation)
			})
		}

		/** Caller owns the file-name locks for the complete consuming operation. */
		static async register_file_url_entries_async( db: AsyncDuckDB, entries: {file:any,name:string}[], generation: number ) {
			const protocol = (await this.lib_async()).DuckDBDataProtocol.HTTP
			this.generation_check(generation)
			const added: string[] = []
			try {
				for(const {file,name} of entries){
					const blob = await $mol_wire_async(file).blob()
					this.generation_check(generation)
					if(!blob) throw new Error(`File "${name}" has no content`)
					const previous = this._file_urls.get(name)
					const url = URL.createObjectURL(blob)
					try {
						await db.dropFile(name)
						this.generation_check(generation)
						await db.registerFileURL(name,url,protocol,false)
						this.generation_check(generation)
					} catch(error) {
						URL.revokeObjectURL(url)
						if(previous && generation === this._generation) await db.registerFileURL(name,previous,protocol,false)
						throw error
					}
					if(previous) URL.revokeObjectURL(previous)
					this._file_urls.set(name,url)
					if(!this._persistent_files.has(name)) added.push(name)
					this._persistent_files.add(name)
				}
			} catch(error) {
				if(added.length && generation === this._generation) await this.drop_file_entries_async(db,added)
				throw error
			}
		}

		static files_temporary_check( names: string[] ) {
			for(const name of names) if(this._persistent_files.has(name)) throw new Error(`File "${name}" is registered persistently; query its view/connection or use a distinct title`)
		}

		static async unregister_files_async( names: string[] ) {
			if(!Array.isArray(names) || names.some(name=>typeof name!=='string'||!name.trim())) throw new Error('File names must be non-empty strings')
			const generation=this._generation
			return this.files_task_async(names,async()=>{
				const db = await this.db_async()
				await this.drop_file_entries_async(db,names,generation)
			})
		}

		static async drop_file_entries_async( db: AsyncDuckDB, names: string[], generation = this._generation ) {
			this.generation_check(generation)
			await db.dropFiles(names)
			// A reset may publish a new registration while the old worker finishes.
			// Never revoke that new generation's URLs or clear its ownership.
			this.generation_check(generation)
			for(const name of names){
				const url = this._file_urls.get(name)
				if(url) URL.revokeObjectURL(url)
				this._file_urls.delete(name)
				this._persistent_files.delete(name)
			}
		}
		
		@ $mol_action
		static connect_json( table_name: string, json?: any ) {
			return $mol_wire_sync($shm_duckdb).connect_json_async( table_name, json! )
		}

		static async connect_json_async( table_name: string, json: any ) {

			const db = await this.db_async()
			const connection = await db.connect()

			// The file is an import resource, not the table. Never share it with
			// another import or retain its complete JSON after materialization.
			const file_name = `__shm_json_${crypto.randomUUID()}.json`

			try {
				await db.registerFileText( file_name, JSON.stringify(json) )
				try {
					await connection.insertJSONFromPath( file_name, { name: table_name, create: true } )
				} finally {
					await db.dropFile(file_name)
				}
				return connection
			} catch( error ) {
				await connection.close()
				throw error
			}
		}
		
		@ $mol_mem
		static connect_files( sources?: any ) {
			this.db_generation()
			return $mol_wire_sync($shm_duckdb).connect_files_async( sources! )
		}
		
		@ $mol_action
		static files_query( files: any, query: string, limit?: number ) {
			return $mol_wire_sync(this).files_query_async(files,query,limit)
		}

		static async files_query_async( files: any, query: string, limit?: number ) {
			return this.files_connection_async(files,async conn=>{
				const table = await conn.query( query )
				return this.get_head_and_rows( limit === undefined ? table : table.slice( 0, limit ) )
			})
		}

		static async files_run_async( files: any, query: string ) {
			await this.files_connection_async(files,async conn=>{await conn.query(query)})
		}

		static async files_connection_async<Result>( files: any, task: (conn:AsyncDuckDBConnection)=>Promise<Result> ): Promise<Result> {
			const generation = this._generation
			const entries = await this.file_entries_async(files)
			if(generation !== this._generation) throw new Error('DuckDB operation was reset')
			return this.files_task_async(entries.map(entry=>entry.name),async()=>{
				this.files_temporary_check(entries.map(entry=>entry.name))
				const db = await this.db_async(),registered: string[] = []
				this.generation_check(generation)
				let conn: AsyncDuckDBConnection | undefined
				try {
					await this.register_file_entries_async(db,entries,registered,generation)
					conn = await db.connect()
					this.generation_check(generation)
					return await task(conn)
				} finally {
					if(generation === this._generation) {
						try { await conn?.close() }
						finally { if(registered.length) await db.dropFiles(registered) }
					}
				}
			})
		}
		
		@ $mol_action
		static json_query( table_name: string, json: any, query: string, limit?: number ) {
			return $mol_wire_sync(this).json_query_async(table_name,json,query,limit)
		}

		static async json_query_async( table_name: string, json: any, query: string, limit?: number ) {
			return this.table_write_async(table_name,()=>this.json_query_write_async(table_name,json,query,limit))
		}

		static async json_query_write_async( table_name: string, json: any, query: string, limit?: number ) {
			const generation = this._generation
			await this.connection_run_async(`DROP TABLE IF EXISTS ${this.sql_identifier(table_name)}`)
			if(generation !== this._generation) throw new Error('DuckDB operation was reset')
			const conn = await this.connect_json_async(table_name,json)
			try {
				const table = await conn.query(query)
				return this.get_head_and_rows_from_vectors(limit === undefined ? table : table.slice(0,limit))
			} finally {
				await conn.close()
			}
		}
		
		@ $mol_action
		static connection_query( query: string, limit?: number ) {
			return $mol_wire_sync( this ).connection_query_async( query, limit )
		}

		static async connection_query_async( query: string, limit?: number ) {
			const conn = await this.connection()
			try {
				const table = await conn.query( query )
				const res = this.get_head_and_rows_from_vectors( limit === undefined ? table : table.slice( 0, limit ) )
				return res
			} finally {
				await conn.close()
			}
		}

		static async connection_run_async( query: string ) {
			const conn = await $shm_duckdb.connection()
			try {
				await conn.query( query )
			} finally {
				await conn.close()
			}
		}

		@ $mol_action
		static table_from_json( table_name: string, rows: readonly Record< string, unknown >[] ) {
			return $mol_wire_sync( this ).table_from_json_async( table_name, rows )
		}

		static async table_from_json_async( table_name: string, rows: readonly Record< string, unknown >[] ) {
			return this.table_write_async(table_name,()=>this.table_from_json_write_async(table_name,rows))
		}

		/** Serialize complete replace/import/read cycles, not just their writes. */
		static async table_write_async< Result >( table_name: string, task: ()=> Promise<Result> ): Promise<Result> {
			const generation = this._generation
			const previous = this._table_writes.get( table_name ) ?? Promise.resolve()
			const current = previous
				.catch( ()=> undefined )
				.then( ()=> {
					if(generation !== this._generation) throw new Error('DuckDB operation was reset')
					return task()
				} )
			this._table_writes.set( table_name, current )

			try {
				return await current
			} finally {
				if( this._table_writes.get( table_name ) === current ) this._table_writes.delete( table_name )
			}
		}

		static async table_from_json_write_async( table_name: string, rows: readonly Record< string, unknown >[] ) {
			const json = JSON.stringify(rows)
			const db = await this.db_async()
			const conn = await db.connect()
			const table = this.sql_identifier( table_name )
			const stage_name = `__shm_wordstat_${crypto.randomUUID().replaceAll('-','')}`
			const stage = this.sql_identifier( stage_name )
			const file_name = `${ stage_name }.json`

			try {
				await db.registerFileText( file_name, json )
				await conn.insertJSONFromPath( file_name, { name: stage_name, create: true } )
				await conn.query( `
					CREATE OR REPLACE TABLE ${ table } AS
					SELECT
						CAST( "Запрос" AS VARCHAR ) AS "Запрос",
						CAST( "Регион" AS VARCHAR ) AS "Регион",
						CAST( "Периодичность" AS VARCHAR ) AS "Периодичность",
						CAST( "Устройство" AS VARCHAR ) AS "Устройство",
						CAST( "Дата" AS DATE ) AS "Дата",
						CAST( "Кол-во" AS BIGINT ) AS "Кол-во",
						CAST( "Доля" AS VARCHAR ) AS "Доля"
					FROM ${ stage };
				` )
			} finally {
				try { await conn.query( `DROP TABLE IF EXISTS ${ stage }` ) }
				finally {
					try { await db.dropFile(file_name) }
					finally { await conn.close() }
				}
			}

			return table_name
		}

		static async files_view_async( view_name: string, files: any, query: string ) {
			if(typeof view_name!=='string'||!view_name.trim()) throw new Error('View name must be a non-empty string')
			const generation=this._generation
			// Reserve the view before resolving metadata/imports: a slow first source
			// must not overwrite a later update after that later call has completed.
			return this.table_write_async(view_name,async()=>{
				const entries=await this.file_entries_async(files)
				this.generation_check(generation)
				return this.files_task_async(entries.map(entry=>entry.name),async()=>{
					const db=await this.db_async()
					this.generation_check(generation)
					const added=entries.filter(entry=>!this._persistent_files.has(entry.name)).map(entry=>entry.name)
					let conn: AsyncDuckDBConnection | undefined
					try{
						// Sources already expose local Blobs. Repeated HTTP/blob registrations
						// retain native/browser memory even after URL revocation in the shipped
						// runtime. BUFFER reuses the bounded allocation for replacement views.
						const registered: string[] = []
						await this.register_file_entries_async(db,entries,registered,generation)
						for(const name of registered) this._persistent_files.add(name)
						conn=await db.connect()
						this.generation_check(generation)
						// One captured database/connection for the whole DDL operation.
						// CREATE OR REPLACE preserves the old view if binding the new query fails.
						await conn.query(`CREATE OR REPLACE VIEW ${this.sql_identifier(view_name)} AS ${query}`)
						this.generation_check(generation)
					}catch(error){
						if(added.length && generation===this._generation) await this.drop_file_entries_async(db,added)
						throw error
					}finally{
						if(conn && generation===this._generation) await conn.close()
					}
				})
			})
		}

		static sql_identifier( name: string ) {
			return '"' + String( name ).replaceAll( '"', '""' ) + '"'
		}

		static sql_string( value: string ) {
			return $shm_duckdb_sql_string(value)
		}

		@ $mol_action
		static table_names() {
			const { rows } = $shm_duckdb.connection_query( `
				SELECT table_name
				FROM information_schema.tables
				WHERE table_schema = 'main'
				ORDER BY table_name
			` )
			return rows.map( row => String( row[0] ) )
		}

		@ $mol_action
		static table_select( table_name: string, limit?: number ) {
			return $shm_duckdb.connection_query(
				`SELECT * FROM ${ $shm_duckdb.sql_identifier( table_name ) }`,
				limit,
			)
		}

		@ $mol_action
		static table_head( table_name: string ) {
			const { head } = $shm_duckdb.connection_query(
				`SELECT * FROM ${ $shm_duckdb.sql_identifier( table_name ) } LIMIT 0`,
			)
			return head
		}

		static get_head_and_rows_from_vectors( table: Awaited<ReturnType<ReturnType<typeof $shm_duckdb.connect_files>[ 'query' ]>> ) {

			const head: string[] = table.schema.fields.map( ( f: any ) => f.name )

			const rows: any[] = []
			;[...table].forEach( ( row: any ) => {
				const json = row.toJSON()
				
				const vals = table.schema.fields.map( ( f: any ) => format_val( f, json[ f.name ] ) )
				const is_vectors = table.schema.fields.every( ( f: any ) => f.type.typeId == 12 )

				if( !is_vectors ) {
					rows.push( vals )
					return
				}
				
				vals.forEach( ( vec: any, col_i: any )=> {
					const vals: any[] = vec.toJSON()
					vals.forEach( ( v, row_i ) => {
						rows[ row_i ] = rows[ row_i ] ?? []
						rows[ row_i ][ col_i ] = format_val( vec, v )
					} )
				} )
			} )

			return { head, rows }

		}

		static get_head_and_rows( table: Awaited<ReturnType<ReturnType<typeof $shm_duckdb.connect_files>[ 'query' ]>> ) {

			const head: string[] = table.schema.fields.map( ( f: any ) => f.name )

			const rows: any[] = [...table].map( ( row: any ) => {
				const json = row.toJSON()
				return table.schema.fields.map( ( f: any ) => format_val( f, json[ f.name ] ) )
			} )

			return { head, rows }

		}

	}

	function format_val( field: any, val: any ) {
		if( val === null ) return ''

		if( field.type.typeId == 8 ) {
			// SQL DATE has no timezone. Arrow exposes its UTC-midnight epoch;
			// interpreting it as local time shifts dates west of UTC one day back.
			const utc = new Date( val )
			if( Number.isNaN(utc.valueOf()) ) throw new RangeError('Invalid DuckDB DATE')
			const date = new $mol_time_moment({ year:utc.getUTCFullYear(), month:utc.getUTCMonth(), day:utc.getUTCDate()-1 })
			return date.toString('DD.MM.YYYY')
		}

		if( field.type.typeId == 7 ) { //Decimal
			return $shm_duckdb_decimal( val, field.type.scale )
		}

		if( typeof val === "bigint" ) return String( val )

		return val
	}

}
